import React from "react";

interface UrbanLegendProps {
  hasBurntUrbanAreas: boolean;
  /** Tree / shrub crowns the graph ignited in this run (adds the Vegetation block). */
  hasBurntCrowns?: boolean;
}

const UrbanLegend: React.FC<UrbanLegendProps> = ({ hasBurntUrbanAreas, hasBurntCrowns = false }) => {
  if (!hasBurntUrbanAreas) return null;
  return <UrbanLegendCard hasBurntCrowns={hasBurntCrowns} />;
};

const UrbanLegendCard: React.FC<{ hasBurntCrowns: boolean }> = ({ hasBurntCrowns }) => {
  // Middle of the left edge, outside the shared bottom row (legendSlots): the
  // bottom-left corner belongs to the loaded simulation's Risk From card, and
  // this one is tall enough that sharing the row pushed that card under the
  // Simulations toolbar.
  return (
    <div style={{
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      left: "20px",
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      padding: "12px 16px",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
      zIndex: 1000,
      fontSize: "12px",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      <div style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "13px" }}>Urban Fire Legend</div>

      <div style={{ marginBottom: "8px" }}>
        <div style={{ fontWeight: "bold", marginBottom: "4px" }}>Buildings</div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "2px" }}>
          <div style={{ width: "16px", height: "16px", backgroundColor: "#ff6b00", marginRight: "8px", border: "1px solid #ff4500", borderRadius: "2px" }} />
          <span>Burning</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "16px", height: "16px", backgroundColor: "#1a1a1a", marginRight: "8px", border: "1px solid #555555", borderRadius: "2px" }} />
          <span>Burnt</span>
        </div>
      </div>

      {hasBurntCrowns && (
        <div style={{ marginBottom: "8px" }}>
          {/* Ignited crowns are discs on the map (UrbanCrownStateLayer) and
              torch then collapse in the 3D layer; colours match the canopy. */}
          <div style={{ fontWeight: "bold", marginBottom: "4px" }}>Vegetation</div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "2px" }}>
            <div style={{ width: "16px", height: "16px", backgroundColor: "#1f7a3d", marginRight: "8px", border: "2px solid #ff6b00", borderRadius: "50%" }} />
            <span>Tree crown burning</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "2px" }}>
            <div style={{ width: "16px", height: "16px", backgroundColor: "#5bb36a", marginRight: "8px", border: "2px solid #ff6b00", borderRadius: "50%" }} />
            <span>Shrub burning</span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ width: "16px", height: "16px", backgroundColor: "#1a1a1a", marginRight: "8px", border: "1px solid #555555", borderRadius: "50%" }} />
            <span>Crown burnt</span>
          </div>
        </div>
      )}

      <div>
        {/* Radiant = solid line, ember = dashed — the same symbology whether the
            heat/embers come from a neighbouring house or from the bushfire. */}
        <div style={{ fontWeight: "bold", marginBottom: "4px" }}>Fire Transfer</div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "2px" }}>
          <div style={{ width: "24px", height: "3px", backgroundColor: "#ff6b00", marginRight: "8px", borderRadius: "1px" }} />
          <span>Convection</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "2px" }}>
          <div style={{ width: "24px", height: "3px", backgroundColor: "#ffcc00", marginRight: "8px", borderRadius: "1px" }} />
          <span>Radiation</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "24px", height: "3px", marginRight: "8px", borderRadius: "1px", background: "repeating-linear-gradient(90deg, #dc13ff 0, #dc13ff 4px, transparent 4px, transparent 8px)" }} />
          <span>Ember</span>
        </div>
      </div>
    </div>
  );
};

export default UrbanLegend;
